import { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { MapPin, Navigation, Loader2, Home, Building } from 'lucide-react';
import { useLocation } from '@/contexts/useLocation';
import { useAuth } from '@/contexts/AuthContext';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import { Card } from './ui/card';
import { AddressService, CustomerAddress } from '@/services/address.service';
import { toast } from 'sonner';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDucpiKqmlow-H0WUXZGDV8gbpd4qmf1Vk';

interface LocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const LocationDialog = ({ open, onOpenChange }: LocationDialogProps) => {
  const { location, setLocation, detectLocation, isLoading } = useLocation();
  const { isAuthenticated, user } = useAuth();
  const [isDetecting, setIsDetecting] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState<CustomerAddress[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  // Fetch saved addresses when dialog opens and user is authenticated
  useEffect(() => {
    if (open && isAuthenticated && user?.id) {
      fetchAddresses();
    }
  }, [open, isAuthenticated, user]);

  const fetchAddresses = async () => {
    if (!user?.id) return;
    
    try {
      setLoadingAddresses(true);
      const addresses = await AddressService.getCustomerAddresses(user.id);
      setSavedAddresses(addresses);
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setLoadingAddresses(false);
    }
  };

  const handleDetectLocation = async () => {
    setIsDetecting(true);
    try {
      detectLocation();
      // Wait a bit for the detection to complete
      setTimeout(() => {
        setIsDetecting(false);
      }, 2000);
    } catch (error) {
      setIsDetecting(false);
    }
  };

  const handleAddressSelect = (address: CustomerAddress) => {
    const locationDetails = {
      address: `${address.address1}${address.address2 ? ', ' + address.address2 : ''}`,
      latitude: 0, // Address API doesn't provide coordinates
      longitude: 0,
      city: address.city,
      state: address.state,
      country: address.country,
      postalCode: address.postcode,
    };

    setLocation(locationDetails);
    localStorage.setItem('user_location', JSON.stringify(locationDetails));
    toast.success('Address selected');
    onOpenChange(false);
  };

  const handlePlaceSelect = async (selectedPlace: any) => {
    if (!selectedPlace?.value?.place_id) return;

    try {
      const [place] = await geocodeByPlaceId(selectedPlace?.value?.place_id);
      console.log('Geocoded place:', place);
   
      // Get place details from Google Places API
      // const response = await fetch(
      //   `https://maps.googleapis.com/maps/api/geocode/json?place_id=${place.value.place_id}&key=${GOOGLE_MAPS_API_KEY}`
      // );
      
      // const data = await response.json();
      const data:any =place;
      // if (data.results && data.results.length > 0) {
         const result = data;
        const addressComponents = result.address_components;
        
        // Extract address components
        const city = addressComponents.find((c: any) => 
          c.types.includes('locality') || c.types.includes('administrative_area_level_2')
        )?.long_name;
        
        const state = addressComponents.find((c: any) => 
          c.types.includes('administrative_area_level_1')
        )?.long_name;
        
        const country = addressComponents.find((c: any) => 
          c.types.includes('country')
        )?.long_name;
        
        const postalCode = addressComponents.find((c: any) => 
          c.types.includes('postal_code')
        )?.long_name;

        const locationDetails = {
          address: result.formatted_address,
          latitude: result.geometry.location.lat,
          longitude: result.geometry.location.lng,
          city,
          state,
          country,
          postalCode,
        };

        
        // Update location in context
        setLocation(locationDetails);
        
        // Store in localStorage
        localStorage.setItem('user_location', JSON.stringify(locationDetails));
        
        toast.success('Location selected');
        
        // Close dialog after selection
        onOpenChange(false);
    
    } catch (error) {
      console.error('Error fetching place details:', error);
      toast.error('Failed to get location details');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader className="mb-6">
          <SheetTitle>Select Delivery Location</SheetTitle>
        </SheetHeader>

        <div className="space-y-4">
          {/* Current Location Section */}
          {location && (
            <Card className="p-4 bg-muted/50">
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-primary mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">Current Location</p>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {location.address}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {location.city && (
                      <p className="text-xs text-muted-foreground">
                        {location.city}
                      </p>
                    )}
                    {location.state && (
                      <p className="text-xs text-muted-foreground">
                        {location.state}
                      </p>
                    )}
                    {location.postalCode && (
                      <span className="text-xs font-medium bg-primary/10 text-primary px-2 py-0.5 rounded">
                        PIN: {location.postalCode}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Saved Addresses Section */}
          {isAuthenticated && savedAddresses.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Saved Addresses</h3>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {savedAddresses.map((address) => (
                  <Card
                    key={address.id}
                    className="p-3 cursor-pointer hover:bg-primary/10 hover:border-primary/50 transition-all duration-200 hover:shadow-md"
                    onClick={() => handleAddressSelect(address)}
                  >
                    <div className="flex items-start gap-3">
                      {address.prefer_delivery === 'home' ? (
                        <Home className="h-4 w-4 text-muted-foreground mt-0.5" />
                      ) : (
                        <Building className="h-4 w-4 text-muted-foreground mt-0.5" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm capitalize">
                            {address?.prefer_delivery?.toUpperCase()}
                          </p>
                          {address.default_address  && (
                            <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                              Default
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-foreground line-clamp-2 mt-1">
                          {address.address1}
                          {address.address2 && `, ${address.address2}`}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-xs text-muted-foreground">
                            {address.city}, {address.state}
                          </p>
                          <span className="text-xs font-medium bg-primary/10 text-primary px-2 py-0.5 rounded">
                            {address.postcode}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {loadingAddresses && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading addresses...</span>
            </div>
          )}          {/* Auto-detect Location Button */}
          <Button
            onClick={handleDetectLocation}
            disabled={isDetecting || isLoading}
            variant="outline"
            className="w-full"
          >
            {isDetecting || isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Detecting...
              </>
            ) : (
              <>
                <Navigation className="mr-2 h-4 w-4" />
                Use Current Location
              </>
            )}
          </Button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                Or search location
              </span>
            </div>
          </div>

          {/* Manual Location Search */}
          <div className="space-y-2">
            <label className="text-sm font-medium">
              Search for your location
            </label>
            {/* <GooglePlacesAutocomplete
              apiKey={GOOGLE_MAPS_API_KEY}
              selectProps={{
                onChange: handlePlaceSelect,
                placeholder: 'Search city, area, or address...',
                isClearable: true,
                styles: {
                  control: (provided) => ({
                    ...provided,
                    borderRadius: '0.375rem',
                    borderColor: 'hsl(var(--input))',
                    minHeight: '2.5rem',
                  }),
                  input: (provided) => ({
                    ...provided,
                    margin: 0,
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isFocused ? 'hsl(var(--accent))' : 'transparent',
                    color: 'hsl(var(--foreground))',
                    cursor: 'pointer',
                  }),
                },
              }}
              apiOptions={{
                region: 'in', // Prefer Indian locations
              }}
            /> */}
            <GooglePlacesAutocomplete
                apiKey={GOOGLE_MAPS_API_KEY}                
                selectProps={{
          //value: address,
          onChange: handlePlaceSelect,
          placeholder: "Search your address...",
          styles: {
            control: (provided) => ({
              ...provided,
              border: "1px solid #ccc",
              borderRadius: "6px",
              height: "45px",
            }),
            option: (provided) => ({
              ...provided,
              fontSize: "14px",
            }),
          },
        }}
              />
          </div>

          {/* Help Text */}
          <p className="text-xs text-muted-foreground text-center">
            Your location helps us show relevant products and delivery options
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
};
